if (!customElements.get('beucher-collection-grid')) {
  customElements.define('beucher-collection-grid', class extends HTMLElement {
    connectedCallback() {
      const reconnecting = Boolean(this.events);
      this.events = new AbortController();
      this.requestVersion = (this.requestVersion || 0) + 1;
      this.desiredURL = this.collectionURL(new URL(window.location.href));
      this.failedAction = null;
      this.grid = this.querySelector('[data-product-grid]');
      this.drawer = this.querySelector('[role="dialog"]');
      this.overlay = this.querySelector('[id^="DrawerOverlay-"]');
      if (!this.grid || !this.drawer) return;
      const signal = this.events.signal;
      this.addEventListener('change', event => {
        if (!this.drawer.contains(event.target) || event.target.tagName !== 'INPUT') return;
        if (event.target.name === 'filter_collection_link') {
          window.location.assign(event.target.value);
          return;
        }
        this.loadPage(this.selectionURL());
      }, { signal });
      this.addEventListener('click', event => this.handleClick(event), { signal });
      this.addEventListener('keydown', event => this.handleKeydown(event), { signal });
      this.addEventListener('pointerover', event => this.loadCarouselImages(event.target.closest('[data-product-id]')), { passive: true, signal });
      this.addEventListener('touchstart', event => this.loadCarouselImages(event.target.closest('[data-product-id]')), { passive: true, signal });
      this.addEventListener('scroll', event => {
        if (!event.target.classList?.contains(`ai-carousel-track-${this.dataset.gridId}`)) return;
        this.loadCarouselImages(event.target.closest('[data-product-id]'));
        this.updateProgress(event.target.closest('[data-product-id]'));
      }, { capture: true, passive: true, signal });
      window.addEventListener('popstate', () => this.loadPage(new URL(window.location.href), { history: false }), { signal });
      this.arrangeCards();
      this.normalizePagination();
      this.setLoading(false);
      this.querySelector('[data-filter-retry]').hidden = true;
      this.syncPhysicalChoices(this.desiredURL);
      if (reconnecting || this.physicalTypes(this.desiredURL).length || this.desiredURL.href !== window.location.href) {
        this.loadPage(this.desiredURL, { history: false, replaceHistory: true });
      }
    }

    handleClick(event) {
      const target = event.target.closest('button, a, [id^="DrawerOverlay-"]');
      if (!target || !this.contains(target)) return;
      if (target.id.startsWith('BtnToggleFilter-')) this.setDrawer(true);
      else if (target.id.startsWith('BtnCloseFilter-') || target.id.startsWith('BtnApplyFilter-') || target === this.overlay) this.setDrawer(false);
      else if (target.id.startsWith('BtnClearFilter-')) {
        this.drawer.querySelectorAll('input').forEach(input => { input.checked = false; });
        const url = new URL(window.location.href);
        Array.from(url.searchParams.keys()).forEach(key => {
          if (key.startsWith('filter.') || key === 'sort_by' || key === 'page' || (this.dataset.physicalTypes && (key === 'beucher_type' || key === 'beucher_page'))) url.searchParams.delete(key);
        });
        this.loadPage(url);
      } else if (target.matches('[data-filter-retry]') && this.failedAction) {
        this.loadPage(this.failedAction.url, this.failedAction.options);
      } else if (target.matches('[data-load-more]')) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        if (this.loading || this.failedAction) return;
        if (this.localCards) this.showMorePhysical(new URL(target.href));
        else this.loadPage(new URL(target.href), { append: true, history: false });
      } else if (target.id.startsWith('LvBackToTop-')) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (target.matches('.arrow-prev, .arrow-next')) {
        event.preventDefault();
        const card = target.closest('[data-product-id]');
        this.loadCarouselImages(card);
        const track = card.querySelector(`.ai-carousel-track-${this.dataset.gridId}`);
        if (track?.clientWidth) {
          const direction = target.classList.contains('arrow-next') ? 1 : -1;
          track.scrollTo({ left: (Math.round(track.scrollLeft / track.clientWidth) + direction) * track.clientWidth, behavior: 'smooth' });
        }
      }
    }

    handleKeydown(event) {
      if (!this.drawer.classList.contains('is-open')) return;
      if (event.key === 'Escape') { event.preventDefault(); this.setDrawer(false); }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.drawer.querySelectorAll('button, input:not(:disabled), a[href]')).filter(element => !element.hidden);
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }

    setDrawer(open) {
      const wasOpen = this.drawer.classList.contains('is-open');
      if (open && !wasOpen) this.previousOverflow = document.body.style.overflow;
      this.drawer.classList.toggle('is-open', open);
      this.drawer.inert = !open;
      this.overlay.classList.toggle('is-visible', open);
      const opener = this.querySelector('[id^="BtnToggleFilter-"]');
      opener.setAttribute('aria-expanded', String(open));
      if (open) {
        document.body.style.overflow = 'hidden';
        this.drawer.querySelector('button')?.focus();
      } else if (wasOpen) {
        document.body.style.overflow = this.previousOverflow || '';
        opener.focus();
      }
    }

    selectionURL() {
      const url = new URL(this.desiredURL || window.location.href);
      const inputs = this.drawer.querySelectorAll('input[data-param-name]');
      inputs.forEach(input => url.searchParams.delete(input.dataset.paramName));
      inputs.forEach(input => {
        if (input.checked) url.searchParams.append(input.dataset.paramName, input.value);
      });
      const priceGroup = this.drawer.querySelector('[data-price-filter]');
      if (priceGroup) {
        const price = priceGroup.querySelector('input:checked');
        if (price) {
          url.searchParams.delete(priceGroup.dataset.minParam);
          url.searchParams.delete(priceGroup.dataset.maxParam);
          const [minimum, maximum] = price.value.split('-');
          url.searchParams.set(priceGroup.dataset.minParam, minimum);
          if (maximum !== 'all') url.searchParams.set(priceGroup.dataset.maxParam, maximum);
        }
      }
      const sort = this.drawer.querySelector('input[name="sort_by_drawer"]:checked');
      if (sort) url.searchParams.set('sort_by', sort.value);
      if (this.dataset.physicalTypes) {
        url.searchParams.delete('beucher_type');
        const types = Array.from(this.drawer.querySelectorAll('[data-physical-choice]:checked'), input => input.value);
        if (types.length) url.searchParams.set('beucher_type', types.join(','));
        url.searchParams.delete('beucher_page');
      }
      url.searchParams.delete('page');
      return this.collectionURL(url);
    }

    physicalTypes(url) {
      if (!this.dataset.physicalTypes) return [];
      const known = ['necklaces-pendants', 'bracelets', 'rings', 'money-clips', 'other'];
      return [...new Set(url.searchParams.getAll('beucher_type').flatMap(value => value.split(',')).filter(value => known.includes(value)))];
    }

    collectionURL(value) {
      const url = new URL(value);
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      if (!this.dataset.physicalTypes) return url;
      const legacyParam = 'filter.p.m.custom.category';
      const legacy = url.searchParams.getAll(legacyParam);
      if (legacy.length) {
        const mapping = { 'Necklaces & Pendants': 'necklaces-pendants', 'Bracelets': 'bracelets', 'Rings': 'rings', 'Money Clips': 'money-clips' };
        if (!url.searchParams.has('beucher_type') && !legacy.includes("Men's Jewelry")) {
          legacy.forEach(value => { if (mapping[value]) url.searchParams.append('beucher_type', mapping[value]); });
        }
        url.searchParams.delete(legacyParam);
        url.searchParams.delete('page');
      }
      const types = this.physicalTypes(url);
      url.searchParams.delete('beucher_type');
      if (types.length) url.searchParams.set('beucher_type', types.join(','));
      if (types.length) url.searchParams.delete('page');
      else url.searchParams.delete('beucher_page');
      return url;
    }

    syncPhysicalChoices(url) {
      if (!this.dataset.physicalTypes) return;
      const selected = this.physicalTypes(url);
      this.drawer.querySelectorAll('[data-physical-choice]').forEach(input => { input.checked = selected.includes(input.value); });
    }

    async fetchSection(url, signal) {
      const requestURL = new URL(url);
      requestURL.searchParams.delete('sections');
      requestURL.searchParams.delete('beucher_type');
      requestURL.searchParams.delete('beucher_page');
      requestURL.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(requestURL, { signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      // Template content stays inert: later-page images and media load only if a
      // matching card is inserted into the visible grid after the batch succeeds.
      const template = document.createElement('template');
      template.innerHTML = await response.text();
      const incoming = template.content.getElementById(this.id);
      const grid = incoming?.querySelector('[data-product-grid]');
      const body = incoming?.querySelector('[data-filter-body]');
      const pagination = incoming?.querySelector('[data-pagination]');
      const wrapper = incoming?.querySelector('[data-total]');
      if (!grid || !body || !pagination || !wrapper) throw new Error('Incomplete collection response');
      return { grid, body, pagination, wrapper };
    }

    async loadPage(value, options = {}) {
      const url = this.collectionURL(value);
      this.desiredURL = url;
      const version = ++this.requestVersion;
      this.request?.abort();
      const controller = new AbortController();
      this.request = controller;
      this.failedAction = null;
      this.setLoading(true);
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const types = this.physicalTypes(url);
        const firstURL = new URL(url);
        if (types.length) firstURL.searchParams.delete('page');
        const first = await this.fetchSection(firstURL, controller.signal);
        if (version !== this.requestVersion || !this.isConnected) return;
        let matching = null;
        if (types.length) {
          matching = [];
          const ids = new Set();
          const visited = new Set();
          const expected = Number(first.wrapper.dataset.total);
          let current = first;
          while (current) {
            if (version !== this.requestVersion || !this.isConnected) return;
            if (Number(current.wrapper.dataset.total) !== expected) throw new Error('Collection changed during filtering');
            current.grid.querySelectorAll('[data-product-id]').forEach(card => {
              if (ids.has(card.dataset.productId)) throw new Error('Duplicate collection page');
              ids.add(card.dataset.productId);
              if (types.includes(card.dataset.physicalType || 'other')) matching.push(card);
            });
            const next = current.pagination.querySelector('[data-load-more]');
            if (!next) break;
            const nextURL = new URL(next.getAttribute('href'), url);
            nextURL.searchParams.delete('section_id');
            nextURL.searchParams.delete('sections');
            if (nextURL.origin !== url.origin || nextURL.pathname !== url.pathname || visited.has(nextURL.href) || ids.size >= expected) throw new Error('Invalid collection pagination');
            visited.add(nextURL.href);
            current = await this.fetchSection(nextURL, controller.signal);
          }
          if (ids.size !== expected) throw new Error('Incomplete collection pages');
        }
        if (version !== this.requestVersion || !this.isConnected) return;

        if (matching) {
          this.localCards = matching;
          const chunks = Math.max(1, Math.min(Math.ceil(matching.length / 24), Number.parseInt(url.searchParams.get('beucher_page'), 10) || 1));
          const tiles = matching.length ? Array.from(first.grid.querySelectorAll('[data-fixed-slot]'), tile => tile.cloneNode(true)) : [];
          tiles.forEach(tile => { tile.hidden = true; });
          this.grid.replaceChildren(...tiles, ...matching.slice(0, chunks * 24).map(card => card.cloneNode(true)));
        } else if (options.append) {
          this.localCards = null;
          const existing = new Set(Array.from(this.grid.querySelectorAll('[data-product-id]'), card => card.dataset.productId));
          first.grid.querySelectorAll('[data-product-id]').forEach(card => {
            if (!existing.has(card.dataset.productId)) this.grid.appendChild(card);
          });
        } else {
          this.localCards = null;
          this.grid.replaceChildren(...first.grid.childNodes);
        }
        if (!options.append || matching) {
          const body = this.drawer.querySelector('[data-filter-body]');
          const scrollTop = body.scrollTop;
          const focused = body.contains(document.activeElement) ? { name: document.activeElement.name, value: document.activeElement.value } : null;
          body.replaceChildren(...first.body.childNodes);
          this.syncPhysicalChoices(url);
          body.scrollTop = scrollTop;
          if (focused) Array.from(body.querySelectorAll('input')).find(input => input.name === focused.name && input.value === focused.value)?.focus({ preventScroll: true });
        }
        this.querySelector('[data-pagination]').replaceChildren(...first.pagination.childNodes);
        this.normalizePagination();
        if (matching) this.updatePhysicalPagination(url);
        this.updateResults(matching ? matching.length : first.wrapper.dataset.total, matching ? Math.ceil(this.grid.querySelectorAll('[data-product-id]').length / 24) || 1 : first.wrapper.dataset.page);
        if (options.replaceHistory) window.history.replaceState({}, '', url);
        else if (options.history !== false && url.href !== window.location.href) window.history.pushState({}, '', url);
      } catch (error) {
        if (version !== this.requestVersion || !this.isConnected) return;
        this.failedAction = { url, options };
        this.querySelector('[data-results-status]').textContent = "Couldn't update products. Previous results are still shown.";
        this.querySelector('[data-filter-retry]').hidden = false;
      } finally {
        clearTimeout(timeout);
        if (version === this.requestVersion && this.isConnected) this.setLoading(false);
      }
    }

    updateResults(total, page) {
      const wrapper = this.querySelector('[data-total]');
      wrapper.dataset.total = total;
      wrapper.dataset.page = page;
      this.querySelector('[data-empty-results]').hidden = Number(total) > 0;
      this.arrangeCards();
      this.querySelector('[data-results-status]').textContent = `${this.grid.querySelectorAll('[data-product-id]').length} of ${total} products`;
    }

    updatePhysicalPagination(url) {
      const pagination = this.querySelector('[data-pagination]');
      pagination.querySelector('[data-load-more]')?.remove();
      const shown = this.grid.querySelectorAll('[data-product-id]').length;
      if (shown >= this.localCards.length) return;
      const nextURL = new URL(url);
      nextURL.searchParams.set('beucher_page', String(Math.floor(shown / 24) + 1));
      const link = document.createElement('a');
      link.id = `LvLoadMore-${this.dataset.gridId}`;
      link.className = 'lv-view-more-btn';
      link.dataset.loadMore = '';
      link.href = nextURL.href;
      link.rel = 'next';
      link.textContent = 'SEE MORE';
      pagination.prepend(link);
    }

    showMorePhysical(url) {
      const shown = this.grid.querySelectorAll('[data-product-id]').length;
      this.grid.append(...this.localCards.slice(shown, shown + 24).map(card => card.cloneNode(true)));
      this.desiredURL = url;
      this.updatePhysicalPagination(url);
      this.updateResults(this.localCards.length, Math.ceil((shown + 24) / 24));
      window.history.pushState({}, '', url);
    }

    setLoading(loading) {
      this.loading = loading;
      this.grid.setAttribute('aria-busy', String(loading));
      this.grid.classList.toggle('is-loading', loading);
      if (loading) {
        this.querySelector('[data-results-status]').textContent = 'Updating products…';
        this.querySelector('[data-filter-retry]').hidden = true;
      }
      const link = this.querySelector('[data-load-more]');
      if (link) {
        link.setAttribute('aria-disabled', String(loading || Boolean(this.failedAction)));
        link.classList.toggle('is-loading', loading || Boolean(this.failedAction));
        link.textContent = loading ? 'LOADING…' : 'SEE MORE';
      }
    }

    normalizePagination() {
      const link = this.querySelector('[data-load-more]');
      if (!link) return;
      const url = new URL(link.getAttribute('href'), window.location.href);
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      link.href = url.href;
    }

    arrangeCards() {
      const cards = Array.from(this.grid.querySelectorAll('[data-product-id]'));
      const pureTiles = Array.from(this.grid.querySelectorAll('[data-fixed-slot]'));
      pureTiles.forEach(tile => { tile.hidden = true; });
      let productIndex = 0, slot = 1;
      const fragment = document.createDocumentFragment();
      while (productIndex < cards.length) {
        const tile = pureTiles.find(item => Number(item.dataset.fixedSlot) === slot);
        if (tile) {
          tile.hidden = false;
          fragment.appendChild(tile);
        } else {
          const card = cards[productIndex++];
          [7, 17, 22].forEach(coverSlot => {
            const cover = card.querySelector(`.cover-slot-${coverSlot}`);
            if (cover) cover.style.display = slot === coverSlot ? 'flex' : 'none';
          });
          fragment.appendChild(card);
        }
        slot++;
      }
      this.grid.appendChild(fragment);
      cards.forEach(card => {
        this.selectMetalImage(card);
        this.updateProgress(card);
      });
    }

    selectMetalImage(card) {
      if (card.dataset.metalImageSelected) return;
      const selected = Array.from(this.drawer.querySelectorAll('input[name="filter_metal"]:checked'), input => input.dataset.label.toLowerCase());
      if (!selected.length) return;
      let variants;
      try { variants = JSON.parse(card.dataset.variants || '[]'); } catch (_) { return; }
      const match = variants.find(variant => variant.options?.some(option => selected.includes(option.toLowerCase().replace(/^\d+(?:\.\d+)?\s*k\s*/i, '').trim())));
      if (!match?.img) return;
      const image = card.querySelector(`.ai-carousel-slide-${this.dataset.gridId}:not(.custom-cover-slide-${this.dataset.gridId}) img`);
      if (!image) return;
      image.removeAttribute('srcset');
      image.src = match.img;
      card.dataset.metalImageSelected = 'true';
    }

    loadCarouselImages(card) {
      if (!card || card.dataset.imagesLoaded === 'true') return;
      card.querySelectorAll('img[data-src]').forEach(image => {
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
        image.removeAttribute('data-srcset');
      });
      card.dataset.imagesLoaded = 'true';
    }

    updateProgress(card) {
      if (!card) return;
      const id = this.dataset.gridId;
      const track = card.querySelector(`.ai-carousel-track-${id}`);
      const progress = card.querySelector(`.slider-progress-container-${id}`);
      const fill = card.querySelector(`.slider-progress-fill-${id}`);
      if (!track || !progress || !fill) return;
      const count = Array.from(track.children).filter(slide => slide.style.display !== 'none').length;
      progress.style.display = count > 1 ? 'block' : 'none';
      card.querySelectorAll('.arrow-prev, .arrow-next').forEach(arrow => { arrow.style.display = count > 1 ? '' : 'none'; });
      fill.style.width = `${100 / Math.max(count, 1)}%`;
      const maximum = track.scrollWidth - track.clientWidth;
      const ratio = maximum > 0 ? Math.max(0, Math.min(track.scrollLeft / maximum, 1)) : 0;
      fill.style.transform = `translate3d(${ratio * Math.max(count - 1, 0) * 100}%, 0, 0)`;
    }

    disconnectedCallback() {
      this.requestVersion++;
      this.request?.abort();
      this.events?.abort();
      if (this.drawer?.classList.contains('is-open')) document.body.style.overflow = this.previousOverflow || '';
    }
  });
}
